import React, { useEffect, useMemo, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import {
  knowledgeBaseApi,
  uploadKnowledgeBaseFile,
} from '@shared/services/apiService/knowledgeBaseApi';
import Spinner from '@shared/components/spinner/Spinner';
import { ConfirmDialog } from '@shared/components/confirmDialog/ConfirmDialog';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useAppDispatch } from '@shared/store';
import { getKnowledgeBaseErrorMessageKey } from '@features/Admin/KnowledgeBase/model/error-mapper';
import { getKnowledgeBaseCapabilities } from '@features/Admin/KnowledgeBase/model/capabilities';
import { mapFileResponseToKnowledgeBaseFile } from '@features/Admin/KnowledgeBase/model/file-mapper';
import {
  hasKnowledgeBaseDuplicateFileName,
  validateKnowledgeBaseUpload,
  type KnowledgeBaseUploadValidationError,
} from '@features/Admin/KnowledgeBase/model/upload-validation';
import type { CampaignKnowledgeBaseFile } from '@features/Admin/KnowledgeBase/model/types';

import KnowledgeBaseFileCard from './components/KnowledgeBaseFileCard';
import KnowledgeBaseFileDetailsDialog from './components/KnowledgeBaseFileDetailsDialog';
import { useStyles } from './KnowledgeBaseTab.styles';
import type { KnowledgeBaseTabProps } from './KnowledgeBaseTab.types';
import type { CampaignFileAgentScope } from '@features/Admin/KnowledgeBase/model/types';

const MIN_AVAILABILITY_BUSY_DURATION_MS = 500;

const KnowledgeBaseTab: React.FC<KnowledgeBaseTabProps> = ({
  campaignId,
  agents,
  isSquadCampaign,
  userRole,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<CampaignKnowledgeBaseFile | null>(null);
  const [detailsFileId, setDetailsFileId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [isAvailabilityBusy, setIsAvailabilityBusy] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const searchQuery = search.trim();
  const activeSearchQuery = debouncedSearch;
  const queryArgs = activeSearchQuery
    ? { id: campaignId, search: activeSearchQuery }
    : { id: campaignId };
  const { canManageFiles, canManageAssignments } = getKnowledgeBaseCapabilities(
    userRole,
    isSquadCampaign,
  );
  const { data, error, isLoading, isFetching } =
    knowledgeBaseApi.useCampaignFileListControllerListQuery(queryArgs, {
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    });
  const [fetchAllFiles, { isFetching: isCheckingDuplicates }] =
    knowledgeBaseApi.useLazyCampaignFileListControllerListQuery();
  const [deleteCampaignFile, { isLoading: isDeleting }] =
    knowledgeBaseApi.useCampaignFileDeleteControllerRemoveMutation();
  const [updateFileAvailability, { isLoading: isUpdatingAvailability }] =
    knowledgeBaseApi.useCampaignFileAvailabilityUpdateControllerUpdateMutation();
  const [reindexKnowledgeBaseFile, { isLoading: isRetrying }] =
    knowledgeBaseApi.useReindexKnowledgeBaseFileMutation();
  const [replaceFileAssignments, { isLoading: isAssigning }] =
    knowledgeBaseApi.useCampaignFileAgentsSetControllerReplaceMutation();

  const files = useMemo(() => (data ?? []).map(mapFileResponseToKnowledgeBaseFile), [data]);
  const showInitialLoader = isLoading && !data;
  const showError = Boolean(error) && !data;
  const isActionsBusy =
    isDeleting || isUpdatingAvailability || isAvailabilityBusy || isRetrying || isAssigning;

  const detailsFile = useMemo(
    () => files.find((file) => file.id === detailsFileId),
    [detailsFileId, files],
  );

  const getUploadValidationMessageKey = (
    validationError: KnowledgeBaseUploadValidationError,
  ): string => `campaignPage.knowledgeBase.upload.validation.${validationError}`;

  const startUpload = async (file: File) => {
    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      await uploadKnowledgeBaseFile({ file, campaignId, onProgress: setUploadProgress });
      dispatch(knowledgeBaseApi.util.invalidateTags([{ type: 'CampaignFiles', id: campaignId }]));
      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('campaignPage.knowledgeBase.upload.successTitle'),
          description: t('campaignPage.knowledgeBase.upload.successDescription', {
            name: file.name,
          }),
        },
      });
    } catch (uploadErrorValue) {
      setUploadError(getKnowledgeBaseErrorMessageKey(uploadErrorValue, 'upload'));
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  const handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    setUploadError(null);
    const validationError = validateKnowledgeBaseUpload(file, campaignId);
    if (validationError) {
      setUploadError(getUploadValidationMessageKey(validationError));
      return;
    }

    try {
      const campaignFiles =
        searchQuery || !data ? await fetchAllFiles({ id: campaignId }).unwrap() : data;
      if (hasKnowledgeBaseDuplicateFileName(campaignFiles, file.name)) {
        setPendingFile(file);
        setIsDuplicateDialogOpen(true);
        return;
      }

      await startUpload(file);
    } catch (duplicateCheckError) {
      setUploadError(getKnowledgeBaseErrorMessageKey(duplicateCheckError, 'upload'));
    }
  };

  const handleConfirmDuplicate = () => {
    if (!pendingFile) return;

    const file = pendingFile;
    setPendingFile(null);
    setIsDuplicateDialogOpen(false);
    void startUpload(file);
  };

  const handleCancelDuplicate = () => {
    setPendingFile(null);
    setIsDuplicateDialogOpen(false);
  };

  const handleDeleteFile = async () => {
    if (!deleteCandidate) return;

    try {
      await deleteCampaignFile({ campaignId, fileId: deleteCandidate.id }).unwrap();
      dispatch(knowledgeBaseApi.util.invalidateTags([{ type: 'CampaignFiles', id: campaignId }]));
      if (detailsFileId === deleteCandidate.id) {
        setDetailsFileId(null);
      }
      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('campaignPage.knowledgeBase.delete.successTitle'),
          description: t('campaignPage.knowledgeBase.delete.successDescription', {
            name: deleteCandidate.fileName,
          }),
        },
      });
    } catch (deleteError) {
      toast.error(t(getKnowledgeBaseErrorMessageKey(deleteError, 'delete')), {
        closeButton: false,
        autoClose: 5000,
      });
    } finally {
      setDeleteCandidate(null);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleRequestDelete = (file: CampaignKnowledgeBaseFile) => {
    setDeleteCandidate(file);
    setIsDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteCandidate(null);
    setIsDeleteDialogOpen(false);
  };

  const handleToggleAvailability = async (file: CampaignKnowledgeBaseFile, isEnabled: boolean) => {
    const startedAt = Date.now();
    setIsAvailabilityBusy(true);

    try {
      await updateFileAvailability({
        campaignId,
        fileId: file.id,
        campaignFileAvailabilityUpdateRequestDto: { isEnabled },
      }).unwrap();
      dispatch(knowledgeBaseApi.util.invalidateTags([{ type: 'CampaignFiles', id: campaignId }]));
    } catch (availabilityError) {
      toast.error(t(getKnowledgeBaseErrorMessageKey(availabilityError, 'availability')), {
        closeButton: false,
        autoClose: 5000,
      });
    } finally {
      const remainingBusyTime = MIN_AVAILABILITY_BUSY_DURATION_MS - (Date.now() - startedAt);

      if (remainingBusyTime > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, remainingBusyTime));
      }

      setIsAvailabilityBusy(false);
    }
  };

  const handleRetry = async (file: CampaignKnowledgeBaseFile) => {
    if (file.status !== 'failed') return;

    try {
      await reindexKnowledgeBaseFile({ fileId: file.id }).unwrap();
      dispatch(knowledgeBaseApi.util.invalidateTags([{ type: 'CampaignFiles', id: campaignId }]));
      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('campaignPage.knowledgeBase.actions.retrySuccessTitle'),
          description: t('campaignPage.knowledgeBase.actions.retrySuccessDescription', {
            name: file.fileName,
          }),
        },
      });
    } catch (retryError) {
      toast.error(t(getKnowledgeBaseErrorMessageKey(retryError, 'retry')), {
        closeButton: false,
        autoClose: 5000,
      });
    }
  };

  const handleReplaceAssignments = async (
    file: CampaignKnowledgeBaseFile,
    agentScope: CampaignFileAgentScope,
    agentIds: string[],
  ): Promise<boolean> => {
    try {
      await replaceFileAssignments({
        campaignId,
        fileId: file.id,
        campaignFileAgentsSetRequestDto:
          agentScope === 'all' ? { agentScope: 'all' } : { agentScope: 'selected', agentIds },
      }).unwrap();
      dispatch(knowledgeBaseApi.util.invalidateTags([{ type: 'CampaignFiles', id: campaignId }]));
      return true;
    } catch (assignmentError) {
      toast.error(t(getKnowledgeBaseErrorMessageKey(assignmentError, 'assignments')), {
        closeButton: false,
        autoClose: 5000,
      });
      return false;
    }
  };

  return (
    <Box className={classes.root}>
      <Stack className={classes.content}>
        <Stack className={classes.header}>
          <Box className={classes.headerCopy}>
            <Typography variant="h5" fontWeight={600}>
              {t('campaignPage.knowledgeBase.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('campaignPage.knowledgeBase.description')}
            </Typography>
          </Box>
          <Stack className={classes.headerActions}>
            {isFetching && !showInitialLoader && (
              <CircularProgress size={20} aria-label={t('campaignPage.knowledgeBase.refreshing')} />
            )}
            {canManageFiles && (
              <>
                <input
                  ref={fileInputRef}
                  id="campaign-knowledge-base-upload"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileSelection}
                  disabled={isUploading || isCheckingDuplicates || isDuplicateDialogOpen}
                  className={classes.hiddenFileInput}
                />
                <Button
                  component="label"
                  htmlFor="campaign-knowledge-base-upload"
                  variant="contained"
                  startIcon={<UploadFileOutlinedIcon />}
                  disabled={
                    isUploading || isCheckingDuplicates || isDuplicateDialogOpen || !campaignId
                  }
                  className={classes.uploadButton}>
                  {t('campaignPage.knowledgeBase.upload.button')}
                </Button>
              </>
            )}
          </Stack>
        </Stack>

        {isUploading && (
          <Stack className={classes.uploadProgress} role="status" aria-live="polite">
            <Typography variant="body2" color="text.secondary">
              {t('campaignPage.knowledgeBase.upload.progress')}
            </Typography>
            <LinearProgress
              variant={uploadProgress === null ? 'indeterminate' : 'determinate'}
              value={uploadProgress ?? undefined}
            />
          </Stack>
        )}

        {uploadError && (
          <Typography role="alert" className={classes.errorMessage}>
            {t(uploadError)}
          </Typography>
        )}

        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          label={t('campaignPage.knowledgeBase.searchLabel')}
          placeholder={t('campaignPage.knowledgeBase.searchPlaceholder')}
          inputProps={{ 'aria-label': t('campaignPage.knowledgeBase.searchLabel') }}
          InputProps={{
            startAdornment: (
              <SearchIcon color="action" className={classes.searchIcon} aria-hidden="true" />
            ),
          }}
          fullWidth
          size="small"
          className={classes.searchInput}
        />

        {showInitialLoader && <Spinner />}

        {showError && (
          <Typography role="alert" className={classes.errorMessage}>
            {t(getKnowledgeBaseErrorMessageKey(error))}
          </Typography>
        )}

        {!showInitialLoader && !showError && files.length === 0 && (
          <Typography color="text.secondary" role="status">
            {activeSearchQuery
              ? t('campaignPage.knowledgeBase.noSearchResults', { query: activeSearchQuery })
              : t('campaignPage.knowledgeBase.noFiles')}
          </Typography>
        )}

        {!showInitialLoader && !showError && files.length > 0 && (
          <Box className={classes.fileList}>
            {files.map((file) => (
              <KnowledgeBaseFileCard
                key={file.id}
                file={file}
                agents={agents}
                canManageFiles={canManageFiles}
                isBusy={isActionsBusy}
                onDelete={handleRequestDelete}
                onDetails={(file) => setDetailsFileId(file.id)}
                onToggle={handleToggleAvailability}
                onRetry={handleRetry}
              />
            ))}
          </Box>
        )}
      </Stack>

      <ConfirmDialog
        showDialogFlag={isDuplicateDialogOpen}
        handleClose={handleCancelDuplicate}
        dialogTitle="campaignPage.knowledgeBase.upload.duplicateTitle"
        dialogContentText={t('campaignPage.knowledgeBase.upload.duplicateDescription', {
          name: pendingFile?.name ?? '',
        })}
        showDialog={setIsDuplicateDialogOpen}
        confirmDialog={handleConfirmDuplicate}
        variant="warning"
      />

      <ConfirmDialog
        showDialogFlag={isDeleteDialogOpen}
        handleClose={handleCancelDelete}
        dialogTitle="campaignPage.knowledgeBase.delete.title"
        dialogContentText={t('campaignPage.knowledgeBase.delete.description', {
          name: deleteCandidate?.fileName ?? '',
        })}
        showDialog={setIsDeleteDialogOpen}
        confirmDialog={handleDeleteFile}
        variant="warning"
      />

      {detailsFile && (
        <KnowledgeBaseFileDetailsDialog
          key={detailsFile.id}
          campaignId={campaignId}
          file={detailsFile}
          agents={agents}
          canManageFiles={canManageFiles}
          canManageAssignments={canManageAssignments}
          isBusy={isActionsBusy}
          onSaveAssignments={handleReplaceAssignments}
          onDelete={handleRequestDelete}
          onClose={() => setDetailsFileId(null)}
        />
      )}
    </Box>
  );
};

export default KnowledgeBaseTab;
